import { faArrowLeft, faCheckCircle, faExclamationCircle, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import Card from '@sharedComponents/Card';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import InputText from '@sharedComponents/InputText';
import Typography from '@sharedComponents/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ForgottenPassword({ onSendEmail, onBack, expired = false }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState(null);
    const [resendClicked, setResendClicked] = useState(false);

    const handleSubmit = async () => {
        if (email.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setStatus('error');
            return;
        }
        try {
            await onSendEmail(email);
            setStatus('success');
        } catch {
            setStatus('error');
        }
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                if (!email) return;
                handleSubmit();
            }}
        >
            <button type="submit" hidden />
            <FlexBox flexDirection="column" gap="l">
                <Typography color="neutral-primary" fontSize="xl" fontWeight="semi-bold">
                    {t('Forgotten password')}
                </Typography>
                <Typography fontSize="l">
                    {t('Enter your username associated with your account to receive a password reset link')}
                </Typography>
                <Card theme="login">
                    <FlexBox flexDirection="column" gap="s">
                        <Typography>{t('Identifier')}</Typography>
                        <InputText
                            placeholder={t('Your usual login')}
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => {
                                setStatus(null);
                                setEmail(e.target.value);
                            }}
                        />
                    </FlexBox>
                </Card>
                {status === null && expired && (
                    <InformationMessage
                        status="error"
                        text={t('This link has expired. Enter your identifier to receive a new reset link.')}
                        icon={faExclamationCircle}
                    />
                )}
                {status === 'success' && (
                    <InformationMessage
                        status="success"
                        text={t(
                            'A reset link has been sent to your email address if it is associated with an account. Check your emails and spam or verify the username entered.',
                        )}
                        icon={faCheckCircle}
                    />
                )}
                {status === 'error' && (
                    <InformationMessage
                        status="error"
                        text={t('The email could not be sent. Please try again.')}
                        icon={faExclamationCircle}
                    />
                )}
                <FlexBox justifyContent="center" gap="m" alignItems="center">
                    <Button label={t('Back')} action={onBack} ghost icon={faArrowLeft} />
                    <Button
                        label={status === 'success' ? t('Resend the link') : t('Receive the link')}
                        action={() => {
                            if (status === 'success') setResendClicked(true);
                            handleSubmit();
                        }}
                        disabled={!email || resendClicked}
                    />
                </FlexBox>
                <FlexBox justifyContent="center">
                    <Button
                        label={t('Help')}
                        action="https://support-tests.zendesk.com/hc/fr/categories/360001329772"
                        ghost
                        icon={faQuestionCircle}
                    />
                </FlexBox>
            </FlexBox>
        </form>
    );
}
